import React, {useEffect} from 'react';

import notifier from '@albert-io/notifier';

import {useCurrentUserPendingTasksQuery} from 'client/SignUp/NewSignUp/signUp.queries.generated';

import ForceOnboarding from './ForceOnboarding';

interface ForceOnboardingGateProps {
  children: React.ReactNode;
}

/**
 * Fetches the current user's pending signup tasks from GraphQL and hands them to
 * ForceOnboarding, which routes the user through the outstanding onboarding steps.
 *
 * Fails open, but never guesses: when the query errors or returns no user, the
 * app (children) renders and the failure is reported — an onboarding branch is
 * never chosen from defaulted data.
 */
export default function ForceOnboardingGate({children}: ForceOnboardingGateProps) {
  const {
    data: pendingData,
    loading: pendingLoading,
    error
  } = useCurrentUserPendingTasksQuery({
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (error) {
      notifier.notify(error, {
        message: 'Pending signup tasks query failed; rendering the app without the onboarding gate',
        tags: 'ForceOnboardingGate'
      });
    }
  }, [error]);

  const currentUser = pendingData?.currentUser;

  if (!pendingLoading && !currentUser) {
    return <>{children}</>;
  }

  return (
    <ForceOnboarding
      pendingSignupTasks={currentUser?.signupPending ?? []}
      signupComplete={currentUser?.signupComplete ?? false}
      pendingTasksLoading={pendingLoading}
      isTeacher={currentUser?.isTeacher ?? false}
    >
      {children}
    </ForceOnboarding>
  );
}
